'use strict';

/**
 * FeaturedCollection — custom element
 *
 * Gère :
 *  - le slider Swiper (slidesPerView dynamique selon data-slider-view)
 *  - la barre de progression horizontale (progressbar custom)
 *  - le changement d'onglet avec lazy-loading des slides via l'API sections Shopify
 */
class FeaturedCollection extends HTMLElement {
  constructor() {
    super();
    this.slider = this.querySelector('[id^="Swiper-"]');
    this.swiperWrapper = this.querySelector('.swiper-wrapper');
    this.tabs = this.querySelector('.ax-featured-collection__tabs-container');
    this.progressEl = this.querySelector('.ax-featured-collection__progress');
    this.progressbarFill = this.querySelector('.ax-featured-collection__progressbar-fill');
    this.prevBtn = this.querySelector('.ax-featured-collection__prev');
    this.nextBtn = this.querySelector('.ax-featured-collection__next');
    this.ctaWrappers = this.querySelectorAll('.ax-featured-collection__cta-wrap');
    this.swiperInstance = null;
    this.abortController = null;
  }

  connectedCallback() {
    this.abortController = new AbortController();
    this.init();
    if (this.tabs) this.setTabListeners();
  }

  disconnectedCallback() {
    if (this.abortController) this.abortController.abort();
    if (this.swiperInstance) this.swiperInstance.destroy();
  }

  init() {
    this.swiperInstance = new window.Swiper(this.slider, {
      slidesPerView: 'auto',
      spaceBetween: 16,
      speed: 300,
      grabCursor: true,
      loop: false,
      watchOverflow: true,
    });

    // Barre de progression : mise à jour à chaque avancement du slider
    this.swiperInstance.on('progress', (_swiper, progress) => {
      this._updateProgressbar(progress);
    });

    // Masquer la barre quand tous les slides tiennent à l'écran
    this.swiperInstance.on('lock', () => this._setProgressVisible(false));
    this.swiperInstance.on('unlock', () => this._setProgressVisible(true));

    // Flèches de navigation prev/next
    this._bindNavButtons();
    // Couvre tous les cas où isBeginning/isEnd peuvent changer :
    // slideChange (clic), reach*/fromEdge (drag avec slidesPerView:'auto'),
    // update (après lazy-load ou changement d'onglet).
    ['slideChange', 'reachBeginning', 'reachEnd', 'fromEdge', 'update'].forEach((evt) => {
      this.swiperInstance.on(evt, () => this._updateNavState());
    });

    // État initial (après init Swiper, isLocked est déjà calculé)
    this._setProgressVisible(!this.swiperInstance.isLocked);
    this._updateProgressbar(0);
    this._updateNavState();
  }

  _bindNavButtons() {
    const signal = this.abortController?.signal;
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', () => this.swiperInstance.slidePrev(), { signal });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', () => this.swiperInstance.slideNext(), { signal });
    }
  }

  _updateNavState() {
    if (!this.swiperInstance) return;
    if (this.prevBtn) this.prevBtn.disabled = this.swiperInstance.isBeginning;
    if (this.nextBtn) this.nextBtn.disabled = this.swiperInstance.isEnd;
    // Toggle des fade masks gauche/droite (cf. CSS @media ≥1600px) :
    // is-beginning → fade gauche masqué, is-end → fade droite masqué
    if (this.slider) {
      this.slider.classList.toggle('is-beginning', this.swiperInstance.isBeginning);
      this.slider.classList.toggle('is-end', this.swiperInstance.isEnd);
    }
  }

  _updateProgressbar(progress) {
    if (!this.progressbarFill) return;
    this.progressbarFill.style.transform = `scaleX(${progress})`;
  }

  _setProgressVisible(visible) {
    if (!this.progressEl) return;
    this.progressEl.classList.toggle('is-hidden', !visible);
  }

  /* -------------------------------------------------------
     Gestion des onglets
  ------------------------------------------------------- */

  setTabListeners() {
    const signal = this.abortController?.signal;
    this.tabs.querySelectorAll('.ax-featured-collection__tab').forEach((tab) => {
      tab.addEventListener(
        'click',
        () => {
          this.activateTab(tab);
          this.activateSlides(tab.dataset.index);
          this.activateCta(tab.dataset.index);
          this.swiperInstance.slideTo(0);
          this._updateProgressbar(0);
          this._updateNavState();
        },
        { signal }
      );
    });
  }

  activateTab(activeTab) {
    this.tabs.querySelectorAll('.ax-featured-collection__tab').forEach((tab) => {
      tab.classList.toggle('active', tab === activeTab);
    });
  }

  async activateSlides(index) {
    // Vérifier si les slides de cet onglet sont déjà dans le DOM
    const existing = this.swiperWrapper.querySelectorAll(`.swiper-slide[data-index="${index}"]`);

    if (existing.length === 0) {
      await this.loadAllSlides();
    }

    // Afficher/masquer les slides selon l'onglet actif
    this.swiperWrapper.querySelectorAll('.swiper-slide').forEach((slide) => {
      slide.classList.toggle('is-hidden', slide.dataset.index !== index);
    });

    this.swiperInstance.update();
  }

  /**
   * Charge toutes les slides de tous les blocs via l'API sections Shopify.
   * Seules les slides dont l'index n'est pas encore dans le DOM sont ajoutées
   * (évite les doublons pour le premier bloc, déjà rendu côté serveur).
   */
  async loadAllSlides() {
    try {
      const sectionId = this.dataset.sectionId;
      const response = await fetch(`/?sections=${encodeURIComponent(sectionId)}`);

      if (!response.ok) {
        throw new Error(`Erreur HTTP ${response.status}`);
      }

      const data = await response.json();
      const html = data[sectionId];

      if (!html) {
        throw new Error(`Section "${sectionId}" introuvable dans la réponse.`);
      }

      const doc = new DOMParser().parseFromString(html, 'text/html');

      // Indices déjà présents dans le DOM
      const existingIndices = new Set(
        [...this.swiperWrapper.querySelectorAll('.swiper-slide')].map((s) => s.dataset.index)
      );

      doc.querySelectorAll('.swiper-slide').forEach((slide) => {
        if (!existingIndices.has(slide.dataset.index)) {
          this.swiperWrapper.appendChild(slide.cloneNode(true));
        }
      });

      document.dispatchEvent(new CustomEvent('reactivateLazyObserver'));
      this.swiperInstance.update();
    } catch (e) {
      console.error('[FeaturedCollection] Impossible de charger les slides :', e);
    }
  }

  activateCta(index) {
    this.ctaWrappers.forEach((wrap) => {
      wrap.classList.toggle('active', wrap.dataset.index === index);
    });
  }
}

if (!customElements.get('featured-collection')) {
  customElements.define('featured-collection', FeaturedCollection);
}
